import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { Grid, IconButton } from '@mui/material';
import Tooltip from './tooltip';
import MunicipioModal from './municipioModal';
import municipiosData from './municipios.json';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PainelCrack from './painelCrack';
import { useDevice } from 'components/DeviceContext.js';
import { getColorByResponse } from 'services/colorService';

const MapaTurismo = () => {
    const svgRef = useRef();
    const zoomRef = useRef(null);
    const { isMobile } = useDevice();
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [tooltipContent, setTooltipContent] = useState('');
    const [selectedUF, setSelectedUF] = useState(null);
    const [selectedMunicipio, setSelectedMunicipio] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [locationIconCoords, setLocationIconCoords] = useState(null);
    const [currentColor, setCurrentColor] = useState(null);
    const [legendData, setLegendData] = useState([]);
    const [transform, setTransform] = useState(d3.zoomIdentity);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`http://127.0.0.1:8000/api/eventos-turismo`);
                const data = await response.json();
                setLegendData(data);

                const geojsonData = feature(municipiosData, municipiosData.objects.municipios);
                const filteredGeojsonData = selectedUF ? geojsonData.features.filter(feature => feature.properties.uf === selectedUF) : geojsonData.features;

                const width = svgRef.current.clientWidth;
                const height = svgRef.current.clientHeight;

                const projection = d3.geoMercator().fitSize([width, height], geojsonData);
                const path = d3.geoPath().projection(projection);

                const svg = d3.select(svgRef.current)
                    .attr('viewBox', `0 0 ${width} ${height}`)
                    .attr('preserveAspectRatio', 'xMidYMid meet');

                svg.selectAll('*').remove();

                // Desenha os caminhos (paths) para os municípios
                const pathsGroup = svg.append('g').attr('class', 'municipios');
                pathsGroup.selectAll('path')
                    .data(filteredGeojsonData)
                    .enter()
                    .append('path')
                    .attr('d', path)
                    .style('fill', 'var(--sem-cor)')
                    .style('stroke', '#fff')
                    .style('stroke-width', '0.2px')
                    .on('mouseover', (event, d) => {
                        const [x, y] = d3.pointer(event);
                        const nomeMunicipio = d.properties.nome;
                        setTooltipPos({ x, y });
                        setTooltipContent(nomeMunicipio);

                        const ibge = d.properties.ibge;
                        const municipio = data.find(item => item.ibge === ibge);
                        const evento = municipio ? municipio.evento : null;

                        if (evento) {
                            const color = getColorByResponse(evento);
                            setCurrentColor(color);
                        } else {
                            setCurrentColor('var(--sem-cor)');
                        }
                    })
                    .on('mouseout', () => {
                        setTooltipContent('');
                    })
                    .on('click', (event, d) => {
                        const { nome, ibge } = d.properties;
                        setModalData({ nome, ibge });
                        setIsModalOpen(true);

                        const [x, y] = path.centroid(d);
                        setLocationIconCoords({ x, y });
                    });

                // Adiciona bandeirinhas coloridas nos municípios com eventos
                const flagsGroup = svg.append('g').attr('class', 'flags'); // Grupo para as bandeiras
                flagsGroup.selectAll('.flag')
                    .data(filteredGeojsonData)
                    .enter()
                    .append('g')
                    .attr('class', 'flag')
                    .attr('transform', d => `translate(${path.centroid(d)[0]}, ${path.centroid(d)[1] - 20})`) // Ajusta a posição
                    .each(function (d) {
                        const ibge = d.properties.ibge;
                        const municipio = data.find(item => item.ibge === ibge);
                        const evento = municipio ? municipio.evento : null;

                        // Apenas insere o ícone de bandeira se o município tiver evento
                        if (evento) {
                            const color = getColorByResponse(evento);
                            d3.select(this)
                                .append('path') // Desenha a bandeira em SVG
                                .attr('d', 'M0 0 L10 0 L10 5 L0 10 Z') // Forma simples de bandeira em SVG
                                .attr('fill', color) // Cor da bandeira conforme o evento
                                .attr('stroke', '#000') // Adiciona uma borda
                                .attr('stroke-width', 0.1);
                        }
                    });

                // Configuração de zoom
                const zoom = d3.zoom()
                    .scaleExtent([1, 8])
                    .on('zoom', (event) => {
                        pathsGroup.attr('transform', event.transform);
                        flagsGroup.attr('transform', event.transform); // Aplica zoom nas bandeiras também
                        setTransform(event.transform);
                    });

                svg.call(zoom);
                zoomRef.current = zoom;

            } catch (error) {
                console.error('Erro ao carregar os dados:', error);
            }
        };

        fetchData();
    }, [selectedUF, selectedMunicipio]);

    useEffect(() => {
        if (selectedMunicipio) {
            const geojsonData = feature(municipiosData, municipiosData.objects.municipios);
            const municipio = geojsonData.features.find(feature => feature.properties.nome === selectedMunicipio);
            if (municipio) {
                const path = d3.geoPath().projection(d3.geoMercator().fitSize([svgRef.current.clientWidth, svgRef.current.clientHeight], geojsonData));
                const [x, y] = path.centroid(municipio);
                setLocationIconCoords({ x, y });

                const { nome, ibge } = municipio.properties;
                setModalData({ nome, ibge });
                setIsModalOpen(true);
            }
        }
    }, [selectedMunicipio]);

    const closeModal = () => {
        setIsModalOpen(false);
        setModalData(null);
    };

    const handleFilterChange = (selectedUF) => {
        setSelectedUF(selectedUF);
        setSelectedMunicipio(null);
    };

    const handleMunicipioSelect = (selectedMunicipio) => {
        setSelectedMunicipio(selectedMunicipio);
    };

    const handleZoomIn = () => {
        const svg = d3.select(svgRef.current);
        svg.transition().call(zoomRef.current.scaleBy, 1.3);
    };

    const handleZoomOut = () => {
        const svg = d3.select(svgRef.current);
        svg.transition().call(zoomRef.current.scaleBy, 1 / 1.3);
    };

    return (
        <Grid container style={{ display: 'flex', justifyContent: 'center', backgroundColor: 'var(--azul)', paddingTop: '9em' }}>
            <Grid item xs={12} md={2} style={{ marginTop: '16px', width: 'auto' }}>
                <PainelCrack
                    municipios={municipiosData.objects.municipios.geometries}
                    onChange={handleFilterChange}
                    onSelectMunicipio={handleMunicipioSelect}
                    legendData={legendData}
                    selectedUF={selectedUF}
                    currentColor={currentColor}
                />
            </Grid>
            <Grid item xs={12} md={1} lg={5} style={{ position: 'relative', minHeight: '78vh', justifyContent: 'center', paddingBottom: isMobile ? '0' : '2em' }}>
                <svg ref={svgRef} style={{ width: '100%', height: '90%' }}></svg>
                <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column' }}>
                    <IconButton onClick={handleZoomIn}>
                        <AddIcon />
                    </IconButton>
                    <IconButton onClick={handleZoomOut}>
                        <RemoveIcon />
                    </IconButton>
                </div>
                {locationIconCoords && (
                    <div
                        style={{
                            position: 'absolute',
                            left: transform.applyX(locationIconCoords.x),
                            top: transform.applyY(locationIconCoords.y),
                        }}
                    >
                        <LocationOnIcon style={{ color: 'red', fontSize: '2rem' }} />
                    </div>
                )}
                {tooltipContent && <Tooltip x={tooltipPos.x} y={tooltipPos.y} content={tooltipContent} />}
                {isModalOpen && <MunicipioModal open={isModalOpen} onClose={closeModal} data={modalData} />}
            </Grid>
        </Grid>
    );
};

export default MapaTurismo;
